import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { useAuth } from '../../../contexts/authContext';
import { FormattedMessage } from 'react-intl';
// material
import { TextField, IconButton, InputAdornment, Alert, Box, Grid, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// component
import Iconify from '../../../components/Iconify';
import { useDisclosure } from '../../../hooks/useDisclosure';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import Pdf from '../../../gdpr/heros-goals-gdpr.pdf';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const auth = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const dialogDisclosure = useDisclosure();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('common.form.email.requirements').required('common.form.email.required'),
    password: Yup.string().required('common.form.password.required'),
    otp: Yup.string().when('otpStep', {
      is: true,
      then: Yup.string().required('common.form.otp.required'),
      otherwise: Yup.string(),
    })
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      otp: ''
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, formikHelpers) => {
      try {
        if (!otpStep) {
          const response = await auth.signIn(values.email, values.password);
          if (!response.message === 'OTP sent to your email.') {
            throw new Error('Login failed');
          }

          setOtpStep(true);
        } else {
          await auth.validateOtp(values.email, values.otp);
        }
      } catch (error) {
        formikHelpers.setFieldError('error', error.message);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box>
            {!otpStep && (
              <>
                <TextField
                  sx={{ mt: 2, mb: 0 }}
                  fullWidth
                  margin="normal"
                  autoComplete="email"
                  type="email"
                  autoFocus
                  label={<FormattedMessage id="login.form.label.email" defaultMessage="Email address" />}
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email ? <FormattedMessage id={formik.errors.email} defaultMessage={formik.errors.email} /> : ' '}
                />

                <TextField
                  sx={{ mt: 1, mb: 0 }}
                  fullWidth
                  margin="normal"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  label={<FormattedMessage id="login.form.label.password" defaultMessage="Password" />}
                  {...getFieldProps('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password ? <FormattedMessage id={formik.errors.password} defaultMessage={formik.errors.password} /> : ' '}
                />
              </>
            )}

            {otpStep && (
              <TextField
                sx={{ mt: 1, mb: 0 }}
                fullWidth
                margin="normal"
                type="text"
                label={<FormattedMessage id="login.form.label.otp" defaultMessage="One-Time Password (OTP)" />}
                {...getFieldProps('otp')}
                error={Boolean(touched.otp && errors.otp)}
                helperText={touched.otp && errors.otp ? <FormattedMessage id={formik.errors.otp} defaultMessage={formik.errors.otp} /> : ' '}
              />
            )}

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{ mt: 1, mb: 2 }}
            >
              {otpStep ? (
                <FormattedMessage id="login.button.verifyOtp" defaultMessage="Verify OTP" />
              ) : (
                <FormattedMessage id="login.button.signin" defaultMessage="Sign In" />
              )}
            </LoadingButton>

            <Grid container alignItems="center">
              <Grid item xs>
                <Link href="#" variant="body2" onClick={dialogDisclosure.open}>
                  <FormattedMessage id="login.forgotPassword" defaultMessage="Forgot password?" />
                </Link>
              </Grid>
              <Grid item xs textAlign="right">
                <Link href={Pdf} variant="body2" target="_blank">
                  <FormattedMessage id="login.privacyPolicy" defaultMessage="Privacy Policy" />
                </Link>
              </Grid>
            </Grid>
            {Boolean(formik.errors.error) && (
              <Alert severity="error">
                <FormattedMessage id={formik.errors.error} defaultMessage={formik.errors.error} />
              </Alert>
            )}
          </Box>
        </Form>
      </FormikProvider>
      {dialogDisclosure.isOpen && <ForgotPasswordDialog open={dialogDisclosure.isOpen} onClose={dialogDisclosure.close} />}
    </>
  );
}
